import type { SnapshotLicenseUsage } from '@ipfabric/api-client/client/schemas';
import { isObject } from '@ipfabric/utils/types/isObject';

/**
 * Field names of `T`, values left unnarrowed. Keeping the keys tied to the generated API type means
 * a renamed or removed field is a type error here, while the values stay `unknown` because the
 * Immutable store erases them - narrowing those is the job of whoever reads them.
 */
type UnnarrowedValues<T> = { [Key in keyof T]: unknown };

type LicenseUsage = NonNullable<SnapshotLicenseUsage>;

export type SnapshotLicenseUsageValue = {
  cloud: UnnarrowedValues<LicenseUsage['cloud']>;
  onPremise: UnnarrowedValues<LicenseUsage['onPremise']>;
};

/**
 * Narrow the `fromJS`-converted `licenseUsage` submap of a snapshot.
 *
 * The Redux store keeps whole snapshots as Immutable structures, so the plain object coming back
 * from `toJS()` has to be narrowed rather than asserted. Only the two sections are checked here;
 * their contents are numeric strings, numbers and nulls depending on the field, and
 * `licenseUsageValues` is what turns them into numbers.
 *
 * Returns `undefined` for anything that is not license usage, including `null` - which is what the
 * API sends for snapshots that do not use the cloud licensing model.
 */
export const toSnapshotLicenseUsage = (
  value: unknown,
): SnapshotLicenseUsageValue | undefined => {
  if (!isObject(value)) {
    return undefined;
  }
  const { cloud, onPremise } = value;
  if (!isObject(cloud) || !isObject(onPremise)) {
    return undefined;
  }
  return { cloud, onPremise };
};
